import axios from 'axios';
// Import your function to get the Supabase token
// Make sure the path is correct for your project structure
import { getAuthToken } from './supabasetoken'; // Or '../utils/auth' or './supabasetoken' etc.

// Base URL for your FastAPI backend (use environment variable)
const API_BASE_URL = process.env.REACT_APP_TWIF_API_HOST;
//const API_BASE_URL = 'https://api.theweekinfantasy.com';

// --- Create a configured Axios instance ---
const apiClient = axios.create({
  baseURL: API_BASE_URL, // Set base URL for all requests
  headers: {
    'Content-Type': 'application/json', // Default content type
  },
  timeout: 10000, // Optional: Set a request timeout (e.g., 10 seconds)
});

// --- Axios Request Interceptor ---
// This function runs BEFORE each request is sent
apiClient.interceptors.request.use(
  async (config) => {
    // Get the latest auth token asynchronously
    const token = await getAuthToken(); // Ensure getAuthToken is async

    if (token) {
      // If token exists, add it to the Authorization header
      config.headers['Authorization'] = `Bearer ${token}`;
      // console.log("Interceptor: Added auth token to request headers."); // Optional debug log
    } else {
      // Optional: Handle cases where token is missing but might be needed
      // Depending on the endpoint, you might want to cancel the request
      // or just let it proceed (backend will handle authorization)
      // console.warn(`Interceptor: No auth token found for request to ${config.url}`);
    }
    return config; // Return the modified config
  },
  (error) => {
    // Handle request configuration errors (e.g., network error before sending)
    console.error('Axios request interceptor error:', error);
    return Promise.reject(error);
  }
);

// --- Axios Response Interceptor (Optional but Recommended for Error Handling) ---
// This function runs AFTER a response is received but BEFORE it reaches your .then/.catch
apiClient.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx causes this function to trigger
    // Simply return the response if it's successful
    return response;
  },
  (error) => {
    // Any status codes outside the range of 2xx cause this function to trigger
    console.error('Axios response interceptor error:', error.response || error.message);

    // You can add global error handling here, e.g.:
    // if (error.response?.status === 401) {
    //   // Handle unauthorized access, maybe trigger logout or token refresh
    //   console.error("Unauthorized access - 401");
    //   // window.location.href = '/login'; // Example redirect
    // } else if (error.response?.status === 403) {
    //   console.error("Forbidden access - 403");
    // }

    // It's important to reject the promise so the error propagates
    // to the component's .catch block
    return Promise.reject(error);
  }
);


// --- API Functions (Now using the apiClient instance) ---

/**
 * Searches for players in the backend.
 * @param {string} searchTerm - The player name query.
 * @returns {Promise<Array<{id: number, name: string, team: string | null, position: string | null}>>}
 */
export const searchPlayers = async (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }
  try {
    // Use apiClient.get, axios handles query params serialization
    const response = await apiClient.get('/players/search', {
      params: { q: searchTerm } // Pass query params in 'params' object
    });
    // Data is directly available in response.data
    return response.data;
  } catch (error) {
    console.error("Error searching players:", error.response?.data || error.message);
    // Re-throw the error for the component to handle (e.g., show error message)
    throw error;
  }
};

/**
 * Saves a manually created team. Requires authentication.
 * @param {{leagueName: string, teamName: string, playerIds: number[]}} teamData
 * @returns {Promise<{id: string, league_type: string, league_name: string | null, team_name: string | null}>}
 */
export const saveManualTeam = async (teamData) => {
  try {
    // Use apiClient.post. Axios automatically stringifies the data object.
    const response = await apiClient.post('/fantasy-teams', teamData);
    return response.data;
  } catch (error) {
    console.error("Error saving manual team:", error.response?.data || error.message);
    throw error; // Let the component handle UI updates based on the error
  }
};

/**
 * Gets Sleeper leagues for a user.
 * @param {string} username - Sleeper username.
 * @param {number} year - NFL season year.
 * @returns {Promise<Array<{league_id: string, name: string}>>}
 */
export const getSleeperLeagues = async (username, year) => {
  try {
    const response = await apiClient.get(`/sleeper/user/${encodeURIComponent(username)}/leagues`, {
      params: { year: year }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Sleeper leagues:", error.response?.data || error.message);
     // Specifically handle 404 from Sleeper user not found as empty array
     if (error.response?.status === 404) {
        return [];
     }
    throw error;
  }
};

/**
 * Gets a user's rosters within a specific Sleeper league.
 * @param {string} leagueId - Sleeper league ID.
 * @param {string} username - Sleeper username.
 * @returns {Promise<Array<{roster_id: string, team_name: string | null}>>}
 */
export const getSleeperRosters = async (leagueId, username) => {
  try {
    const response = await apiClient.get(`/sleeper/league/${encodeURIComponent(leagueId)}/rosters`, {
      params: { username: username }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Sleeper rosters:", error.response?.data || error.message);
    // Handle 404 for league/roster not found? Or let backend handle?
    // Let's re-throw for now, component can decide based on status.
    throw error;
  }
};

/**
 * Syncs a selected Sleeper team to the backend. Requires authentication.
 * @param {{sleeperLeagueId: string, sleeperRosterId: string, leagueName?: string, teamName?: string}} syncData
 * @returns {Promise<{id: string, league_type: string, league_name: string | null, team_name: string | null}>}
 */
export const syncSleeperTeam = async (syncData) => {
  try {
    const response = await apiClient.post('/fantasy-teams/sync/sleeper', syncData);
    return response.data;
  } catch (error) {
    console.error("Error syncing Sleeper team:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetches all fantasy teams belonging to the authenticated user.
 * @returns {Promise<Array<{id: string, league_name: string | null, team_name: string | null, league_type: string}>>}
 */
export const getUserFantasyTeams = async () => {
    try {
      // Assuming your backend endpoint is GET /api/fantasy-teams
      const response = await apiClient.get('/teams');
      return response.data; // Expects an array of team objects
    } catch (error) {
      console.error("Error fetching user fantasy teams:", error.response?.data || error.message);
      throw error; // Let the component handle UI updates
    }
  };
  
  /**
   * Fetches a unique list of players for the given fantasy team IDs.
   * @param {string[]} teamIds - An array of fantasy team UUIDs.
   * @returns {Promise<Array<{id: number, name: string, team: string | null, position: string | null}>>}
   */
  export const getPlayersForTeams = async (teamIds) => {
    if (!teamIds || teamIds.length === 0) {
      return []; // No teams selected, return empty array
    }
    try {
      // Assuming backend endpoint is GET /api/fantasy-teams/players?team_ids=uuid1,uuid2,...
      const params = new URLSearchParams({ team_ids: teamIds.join(',') });
      const response = await apiClient.get(`/rosters?${params.toString()}`);
      return response.data; // Expects an array of unique player objects
    } catch (error) {
      console.error("Error fetching players for teams:", error.response?.data || error.message);
      throw error;
    }
  };

 /**
 * Fetches aggregated snippet stats (sentiment AND classification) for given players within a date range.
 * @param {number[]} playerIds - An array of internal player IDs.
 * @param {number} days - The lookback period in days (1, 3, or 7).
 * @returns {Promise<{
 * total_snippets: number,
 * positive_count: number,
 * negative_count: number,
 * net_score: number,
 * classification_counts: Record<string, number>
 * }>} The aggregated stats object.
 */
export const getSnippetStatsForPlayers = async (playerIds, days) => {
  // Basic input validation on the frontend is good practice
  if (!Array.isArray(playerIds) || playerIds.length === 0 || typeof days !== 'number' || days < 1) {
    console.warn("getSnippetStatsForPlayers: Invalid input provided.", { playerIds, days });
    // Return a default structure matching the expected response
    return {
        total_snippets: 0,
        positive_count: 0,
        negative_count: 0,
        net_score: 0,
        classification_counts: {}
    };
  }

  try {
    // Construct query parameters
    const params = {
      player_ids: playerIds.join(','), // Join array into comma-separated string
      days: days,
    };

    // Make the GET request using the configured axios instance
    // Use the endpoint name you defined: /snippetstats
    // Assuming the router prefix was /api/snippets
    const response = await apiClient.get('/snippetstats', { params });

    // Axios automatically parses JSON, data is in response.data
    // Add a check for the expected structure or return default if needed
    return response.data || { // Return data or default if response.data is null/undefined
        total_snippets: 0,
        positive_count: 0,
        negative_count: 0,
        net_score: 0,
        classification_counts: {}
    };
  } catch (error) {
    // Log the error details from axios (error.response contains server response)
    console.error("Error fetching snippet stats:", error.response?.data || error.message);
    // Re-throw the error so the calling component (UserPage) can handle it
    // (e.g., show an error message to the user)
    throw error;
  }
};

// --- NEW Yahoo API Functions ---

/**
 * Initiates the Yahoo OAuth login flow by redirecting the user.
 * This is NOT an API call that returns data, but a navigation action.
 */
export const redirectToYahooLogin = () => {
  // The backend endpoint /auth/yahoo/login will handle the redirect to Yahoo.
  window.location.href = `${API_BASE_URL}/auth/yahoo/login`;
};

/**
 * Gets Yahoo leagues for the authenticated user for a given year.
 * @param {number} year - NFL season year.
 * @returns {Promise<Array<{league_key: string, name: string, season: string}>>}
 */
export const getYahooLeagues = async (year) => {
  try {
    const response = await apiClient.get('/yahoo/leagues', { // Path relative to router prefix
      params: { year: year }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Yahoo leagues:", error.response?.data || error.message);
    if (error.response?.status === 401) {
        // Handle 401 specifically, maybe prompt user to re-login with Yahoo
        // alert("Yahoo authentication is required or has expired. Please connect your Yahoo account.");
    }
    throw error;
  }
};

/**
 * Gets the authenticated user's teams within a specific Yahoo league.
 * @param {string} leagueKey - Yahoo league key.
 * @returns {Promise<Array<{team_key: string, name: string}>>}
 */
export const getYahooUserTeamsInLeague = async (leagueKey) => {
  try {
    const response = await apiClient.get(`/yahoo/leagues/${encodeURIComponent(leagueKey)}/teams`); // Path relative to router prefix
    return response.data;
  } catch (error) {
    console.error(`Error fetching Yahoo teams for league ${leagueKey}:`, error.response?.data || error.message);
    if (error.response?.status === 401) {
        // alert("Yahoo authentication is required or has expired. Please connect your Yahoo account.");
    }
    throw error;
  }
};

/**
 * Syncs a selected Yahoo team to the backend. Requires authentication.
 * @param {{yahooLeagueKey: string, yahooTeamKey: string, leagueName?: string, teamName?: string}} syncData
 * @returns {Promise<object>} The synced fantasy team object from the backend.
 */
export const syncYahooTeam = async (syncData) => {
  try {
    // The endpoint in fantasy_teams_routes.py is /sync/yahoo
    const response = await apiClient.post('/sync/yahoo', syncData); // Path relative to router prefix
    return response.data;
  } catch (error) {
    console.error("Error syncing Yahoo team:", error.response?.data || error.message);
    throw error;
  }
};

// --- NEW: Delete User Fantasy Teams ---
/**
 * Deletes specified fantasy teams for the authenticated user.
 * @param {string[]} teamIds - An array of fantasy team UUIDs to delete.
 * @returns {Promise<void>} Resolves on successful deletion (204 No Content).
 * @throws {Error} Throws an error if the deletion fails.
 */
export const deleteUserFantasyTeams = async (teamIds) => {
  if (!teamIds || teamIds.length === 0) {
    // console.log("No team IDs provided for deletion.");
    return; // Or throw new Error("No team IDs provided");
  }

  try {
    // Axios DELETE requests send data in a `data` property within the config object.
    const response = await apiClient.delete('/fantasy-teams', {
      data: { team_ids: teamIds }, // FastAPI endpoint expects { "team_ids": [...] }
    });

    // A 204 No Content response indicates success but has no body.
    if (response.status === 204) {
      // console.log('Teams successfully deleted');
      return true; // Indicate success
    }
    
    // For any other status, it might be an unexpected success response or an error handled by catch.
    // Depending on API design, other 2xx codes might also be success.
    // If the API strictly uses 204 for this operation, this block might not be strictly needed.
    // console.warn('Unexpected successful response status:', response.status);
    return response.data; // Or handle as an unexpected scenario

  } catch (error) {
    // console.error('Error deleting fantasy teams:', error);
    let errorMessage = 'Failed to delete teams.';
    if (error.response && error.response.data && error.response.data.detail) {
      if (Array.isArray(error.response.data.detail)) {
        errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
      } else {
        errorMessage = error.response.data.detail;
      }
    }
    throw new Error(errorMessage);
  }
};

// --- NEW: Resync Fantasy Team ---
/**
 * Requests the backend to re-sync a specific fantasy team with its platform.
 * @param {string} teamId - The UUID of the fantasy team to re-sync.
 * @returns {Promise<object>} The updated fantasy team object from the backend.
 * @throws {Error} Throws an error if the re-sync fails.
 */
export const resyncFantasyTeam = async (teamId) => {
  if (!teamId) {
    throw new Error("Team ID is required for re-sync.");
  }

  try {
    const response = await apiClient.put(`/fantasy-teams/${teamId}/resync`);
    // Ensure data is returned; backend should send the updated team object with a 200 OK
    if (response.status === 200 && response.data) {
      return response.data;
    }
    // Handle cases where response might be unexpected for a successful resync
    throw new Error(`Re-sync request for team ${teamId} returned status ${response.status} but no data.`);

  } catch (error) {
    console.error(`Error re-syncing team ${teamId}:`, error.response?.data || error.message);
    let errorMessage = 'Failed to re-sync team.';
    if (error.response && error.response.data && error.response.data.detail) {
      if (Array.isArray(error.response.data.detail)) {
        errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
      } else {
        errorMessage = error.response.data.detail;
      }
    }
    // If the error object itself has a message and no specific detail from response, use it (e.g. network error)
    else if (error.message) {
        errorMessage = error.message;
    }
    throw new Error(errorMessage);
  }
};

// --- Export the configured client if you need to use it directly elsewhere (optional) ---
// export default apiClient;

/**
 * Fetches all user dashboard data in a single optimized API call
 * @param {number} days - Number of past days for snippets (0 for all time)
 * @returns {Promise<object>} - Complete dashboard data including teams, players, and snippets
 */
export const getUserDashboardBulk = async (days = 7) => {
    try {
        const response = await apiClient.get('/dashboard-bulk', {
            params: { days: days }
        });
        
        console.log(`Bulk dashboard data fetched: ${response.data.teams?.length} teams, ${response.data.players?.length} players, ${Object.keys(response.data.snippets || {}).length} players with snippets`);
        
        return response.data;
    } catch (error) {
        console.error('Error fetching bulk dashboard data:', error.response?.data || error.message);
        throw error;
    }
};
